import React, { useMemo } from "react";
import { Box, FormControl, MenuItem, Select, ListItemText } from "@mui/material";
import warning from "../../images/warning.png";
import BasicTable from "../basicTable/basicTable";
import TablePagination from "../basicTable/tablePagination/tablePagination";
import useDashboardTableLayout, {
  createDashboardRowClickHandler,
  getDashboardContentFitModifiers,
  getDashboardEmptyModifiers,
  getDashboardLoadingModifiers,
  getDashboardTableSx,
  useDashboardDisplayColumns,
} from "../../hooks/useDashboardTableLayout";
import { tableStyle, tableStyleOverDue } from "../../style/common";
import "../../pages/GeneralServices/NewConnection/NewConnectionTableDashboard.scss";

/**
 * Dual-table service dashboard (normal + overdue) — same layout as New Connection
 * on large / medium / small screens.
 */
const ServiceDashboardTables = ({
  enabled,
  isLoadingNormal = false,
  isLoadingOverdue = false,
  normalTitle,
  overdueTitlePrefix = "Over Due Applications",
  overdueCount,
  normalColumns,
  overdueColumns,
  normalData = [],
  overdueData = [],
  onRowClick,
  passRowOnClick = false,
  mobileFields: mobileFieldNames = [
    "applicationId",
    "applicantName",
    "applicationStatusId",
  ],
  normalTableProps = {},
  overdueTableProps = {},
  renderNormalHeader,
  showCompanyFilter = false,
  companies = [],
  selectedOfficeCode,
  onOfficeCodeChange,
  comfortableRows = false,
}) => {
  const {
    dashboardRef,
    isSmallScreen,
    isMediumLayout,
    isDashboardFit,
    dashboardVisibleRows,
    dashboardRowHeight,
    dashboardClassName,
    dashboardBoxSx,
  } = useDashboardTableLayout({
    enabled,
    isLoading: isLoadingNormal,
    isLoadingSecondary: isLoadingOverdue,
    comfortableRows,
  });

  const mobileFields = useMemo(
    () => new Set(mobileFieldNames),
    [mobileFieldNames]
  );

  const { displayNormal, displayOverdue } = useDashboardDisplayColumns(
    isSmallScreen,
    isMediumLayout,
    mobileFields,
    normalColumns,
    overdueColumns
  );

  const dashboardTableSx = useMemo(() => getDashboardTableSx(tableStyle), []);
  const dashboardTableSxOverDue = useMemo(
    () => getDashboardTableSx(tableStyleOverDue),
    []
  );

  const rowClickHandler = useMemo(
    () =>
      onRowClick
        ? createDashboardRowClickHandler(onRowClick, { passRow: passRowOnClick })
        : undefined,
    [onRowClick, passRowOnClick]
  );

  const normalRows = normalData?.slice(
    0,
    isDashboardFit ? dashboardVisibleRows : 20
  );
  const overdueRows = overdueData?.slice(
    0,
    isDashboardFit ? dashboardVisibleRows : 20
  );

  const overdueTitle = `${overdueTitlePrefix} ( ${overdueCount ?? "-"} )`;

  const isNormalEmpty =
    !isLoadingNormal && (!normalRows || normalRows.length === 0);
  const isOverdueEmpty =
    !isLoadingOverdue && (!overdueRows || overdueRows.length === 0);
  const isNormalContentFit =
    !isLoadingNormal && normalRows && normalRows.length > 0;
  const isOverdueContentFit =
    !isLoadingOverdue && overdueRows && overdueRows.length > 0;

  const normalEmpty = getDashboardEmptyModifiers(isNormalEmpty);
  const overdueEmpty = getDashboardEmptyModifiers(isOverdueEmpty);
  const normalContent = getDashboardContentFitModifiers(
    isNormalContentFit,
    isLoadingNormal,
    isDashboardFit
  );
  const overdueContent = getDashboardContentFitModifiers(
    isOverdueContentFit,
    isLoadingOverdue,
    isDashboardFit
  );
  const normalLoading = getDashboardLoadingModifiers(isLoadingNormal);
  const overdueLoading = getDashboardLoadingModifiers(isLoadingOverdue);

  const isDualEmpty = isNormalEmpty && isOverdueEmpty;

  if (!enabled) return null;

  return (
    <Box
      ref={dashboardRef}
      className={`form-box nc-dashboard nc-dashboard--shared-services${dashboardClassName}`}
      sx={dashboardBoxSx}
    >
      <div
        className={`nc-dashboard-grid${
          isDualEmpty ? " nc-dashboard-grid--dual-empty" : ""
        }`}
      >
        <Box
          className={`application-table nc-dashboard-card nc-dashboard-card--normal${normalEmpty.card}${normalContent.card}`}
          sx={
            isSmallScreen
              ? { width: "100%", maxWidth: "100%", marginRight: 0, flex: "none" }
              : undefined
          }
        >
          <div
            className={`nc-dashboard-table-section nc-dashboard-table-section--normal${normalEmpty.section}${normalContent.section}`}
          >
            {showCompanyFilter ? (
              <Box
                className="nc-dashboard-header"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <h3 className="nc-dashboard-title nc-dashboard-title--normal">
                    {normalTitle}
                  </h3>
                </Box>
                <Box sx={{ marginBottom: "1px" }}>
                  <FormControl sx={{ minWidth: 200 }} variant="outlined">
                    <Select
                      value={selectedOfficeCode}
                      onChange={(e) => onOfficeCodeChange?.(e.target.value)}
                      sx={{
                        backgroundColor: "white",
                        "& .MuiSelect-select": {
                          padding: "4px 10px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid lightgray",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid lightgray",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid lightgray",
                        },
                      }}
                      renderValue={(selected) => {
                        const company = companies.find((c) => c.code === selected);
                        return company ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {company.name}
                            </span>
                            <img
                              src={company.logo}
                              alt={`${company.name} logo`}
                              style={{ width: 22, height: 22, marginLeft: 8 }}
                            />
                          </div>
                        ) : (
                          "Select a company"
                        );
                      }}
                    >
                      {companies.map((company) => (
                        <MenuItem key={company.code} value={company.code}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <ListItemText primary={company.name} />
                            <img
                              src={company.logo}
                              alt={`${company.name} logo`}
                              style={{ width: 22, height: 22, marginLeft: 8 }}
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ) : renderNormalHeader ? (
              renderNormalHeader({
                isSmallScreen,
                isDashboardFit,
                dashboardRowHeight,
              })
            ) : (
              <div className="nc-dashboard-header">
                <h3 className="nc-dashboard-title nc-dashboard-title--normal">
                  {normalTitle}
                </h3>
              </div>
            )}
            <div
              className={`div-datagrid nc-dashboard-scroll${normalEmpty.scroll}${normalContent.scroll}${normalLoading.scroll}`}
            >
              <BasicTable
                columns={displayNormal}
                data={normalRows}
                handleRowClick={rowClickHandler}
                sx={isDashboardFit ? dashboardTableSx : tableStyle}
                rowHeight={isDashboardFit ? dashboardRowHeight : 37}
                showFooter={false}
                pageSize={isDashboardFit ? dashboardVisibleRows : 20}
                rowsPerPageOptions={[18, 30, 60]}
                rowsPageOption={false}
                isLoading={isLoadingNormal}
                fillLoadingHeight={false}
                getRowStyle={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0
                    ? tableStyle.evenRow
                    : tableStyle.oddRow
                }
                {...normalTableProps}
              />
            </div>
            <div className="nc-dashboard-pagination">
              <TablePagination
                rowsPerPageOptions={[18, 30, 60]}
                count={normalTableProps.totalRecords ?? 0}
                rowsPerPage={20}
                page={1}
                currentPage={
                  normalTableProps.currentPage
                    ? normalTableProps.currentPage
                    : 1
                }
                totalPages={
                  normalTableProps.totalPages
                    ? normalTableProps.totalPages
                    : 1
                }
                totalRecords={normalTableProps.totalRecords ?? 0}
                onChangePage={() => {}}
                onChangeRowsPerPage={() => {}}
                rowsPageOption={false}
                nextPage={normalTableProps.nextPage}
                previousPage={normalTableProps.previousPage}
                firstPage={normalTableProps.firstPage}
                lastPage={normalTableProps.lastPage}
              />
            </div>
          </div>
        </Box>

        <Box
          className={`application-table nc-dashboard-card nc-dashboard-card--overdue${overdueEmpty.card}${overdueContent.card}`}
          sx={
            isSmallScreen
              ? { width: "100%", maxWidth: "100%", marginRight: 0, flex: "none" }
              : undefined
          }
        >
          <div
            className={`nc-dashboard-table-section nc-dashboard-table-section--overdue${overdueEmpty.section}${overdueContent.section}`}
          >
            <div className="nc-dashboard-header">
              <h3 className="nc-dashboard-title nc-dashboard-title--overdue">
                <img
                  src={warning}
                  alt="icon"
                  className="nc-dashboard-warning-icon"
                />
                {overdueTitle}
              </h3>
            </div>
            <div
              className={`div-datagrid nc-dashboard-scroll${overdueEmpty.scroll}${overdueContent.scroll}${overdueLoading.scroll}`}
            >
              <BasicTable
                columns={displayOverdue}
                data={overdueRows}
                handleRowClick={rowClickHandler}
                sx={isDashboardFit ? dashboardTableSxOverDue : tableStyleOverDue}
                rowHeight={
                  isDashboardFit ? dashboardRowHeight : 37.5
                }
                showFooter={false}
                pageSize={isDashboardFit ? dashboardVisibleRows : 20}
                rowsPerPageOptions={[18, 30, 60]}
                rowsPageOption={false}
                isLoading={isLoadingOverdue}
                fillLoadingHeight={false}
                getRowStyle={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0
                    ? tableStyleOverDue.evenRow
                    : tableStyleOverDue.oddRow
                }
                {...overdueTableProps}
              />
            </div>
            <div className="nc-dashboard-pagination">
              <TablePagination
                rowsPerPageOptions={[18, 30, 60]}
                count={overdueTableProps.totalRecords ?? 0}
                rowsPerPage={20}
                page={1}
                currentPage={
                  overdueTableProps.currentPage
                    ? overdueTableProps.currentPage
                    : 1
                }
                totalPages={
                  overdueTableProps.totalPages
                    ? overdueTableProps.totalPages
                    : 1
                }
                totalRecords={overdueTableProps.totalRecords ?? 0}
                onChangePage={() => {}}
                onChangeRowsPerPage={() => {}}
                rowsPageOption={false}
                nextPage={overdueTableProps.nextPage}
                previousPage={overdueTableProps.previousPage}
                firstPage={overdueTableProps.firstPage}
                lastPage={overdueTableProps.lastPage}
              />
            </div>
          </div>
        </Box>
      </div>
    </Box>
  );
};

export default ServiceDashboardTables;
